import React from 'react';
import { Dialog, DialogContent, DialogTitle, DialogClose } from '../../../components/ui/dialog';
import { ArrowLeft } from 'lucide-react';

interface FilterModalsProps {
  showPropertyTypeModal: boolean;
  setShowPropertyTypeModal: (show: boolean) => void;
  // Amenities modal removed
  showSquareFeetModal: boolean;
  setShowSquareFeetModal: (show: boolean) => void;
  showDaysOnMarketModal: boolean;
  setShowDaysOnMarketModal: (show: boolean) => void;
  propertyTypeWarning: string;
  setPropertyTypeWarning: (warning: string) => void;
  resetBouncing: boolean;
  daysOnMarketResetBouncing: boolean;
  pendingDaysOnMarket: number | null;
  setPendingDaysOnMarket: (value: number | null) => void;
  setDaysOnMarketResetBouncing: (value: boolean) => void;
  // Amenities props removed
  minSquareFeet: string;
  setMinSquareFeet: (value: string) => void;
  maxSquareFeet: string;
  setMaxSquareFeet: (value: string) => void;
  pendingMinSquareFeet: string;
  setPendingMinSquareFeet: (value: string) => void;
  pendingMaxSquareFeet: string;
  setPendingMaxSquareFeet: (value: string) => void;
  propertyTypes: string[];
  getSelectedPropertyTypes: () => string[];
  togglePropertyType: (type: string) => void;
  activeFilterTab: string;
  setSelectedPropertyTypesRent: (types: string[]) => void;
  setSelectedPropertyTypesBuySold: (types: string[]) => void;
  setPropertyTypePillTouchedRent: (value: boolean) => void;
  setPropertyTypePillTouchedBuySold: (value: boolean) => void;
  setResetBouncing: (value: boolean) => void;
  rentPropertyTypes: string[];
  buyPropertyTypes: string[];
  setFilters: (filters: any | ((prev: any) => any)) => void;
}

export function FilterModals({
  showPropertyTypeModal,
  setShowPropertyTypeModal,
  // Amenities modal removed
  showSquareFeetModal,
  setShowSquareFeetModal,
  showDaysOnMarketModal,
  setShowDaysOnMarketModal,
  propertyTypeWarning,
  setPropertyTypeWarning,
  resetBouncing,
  daysOnMarketResetBouncing,
  pendingDaysOnMarket,
  setPendingDaysOnMarket,
  setDaysOnMarketResetBouncing,
  // Amenities props removed
  minSquareFeet,
  setMinSquareFeet,
  maxSquareFeet,
  setMaxSquareFeet,
  pendingMinSquareFeet,
  setPendingMinSquareFeet,
  pendingMaxSquareFeet,
  setPendingMaxSquareFeet,
  propertyTypes,
  getSelectedPropertyTypes,
  togglePropertyType,
  activeFilterTab,
  setSelectedPropertyTypesRent,
  setSelectedPropertyTypesBuySold,
  setPropertyTypePillTouchedRent,
  setPropertyTypePillTouchedBuySold,
  setResetBouncing,
  rentPropertyTypes,
  buyPropertyTypes,
  setFilters,
}: FilterModalsProps) {
  // Track whether we're saving the square feet modal
  const [isSavingSquareFeet, setIsSavingSquareFeet] = React.useState(false);
  // Initialize pending square feet values when modal opens
  React.useEffect(() => {
    if (showSquareFeetModal) {
      setPendingMinSquareFeet(minSquareFeet);
      setPendingMaxSquareFeet(maxSquareFeet);
    }
  }, [showSquareFeetModal, minSquareFeet, maxSquareFeet, setPendingMinSquareFeet, setPendingMaxSquareFeet]);
  // AMENITIES constant removed - amenities filter was removed


  return (
    <>
      {/* Property Type Modal */}
      {showPropertyTypeModal && (
        <Dialog open={showPropertyTypeModal} onOpenChange={open => {
          setShowPropertyTypeModal(open);
          if (!open) {
            setPropertyTypeWarning('');
          }
          // Mark the pill as touched when the modal is opened (user has interacted with property type selection)
          if (open) {
            if ((activeFilterTab as string) === 'rent') {
              setPropertyTypePillTouchedRent(true);
            } else if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
              setPropertyTypePillTouchedBuySold(true);
            }
          }
        }}>
          <DialogContent
            className={`fixed left-1/2 top-[60%] -translate-x-1/2 max-w-md w-full bg-background rounded-3xl shadow-2xl p-0 overflow-hidden text-foreground border border-border max-h-[80vh] overflow-y-auto`}
            hideDefaultClose={true}
          >
            <DialogClose asChild>
              <button 
                className="flex items-center justify-center bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors absolute top-4 left-4"
                aria-label="Close property type modal"
                style={{ height: '40px', width: '40px' }}
                onClick={e => {
                  if (getSelectedPropertyTypes().length === 0) {
                    // Silently reset to default property types instead of showing warning
                    if ((activeFilterTab as string) === 'rent') {
                      setSelectedPropertyTypesRent([...rentPropertyTypes]);
                      setPropertyTypePillTouchedRent(false);
                    } else if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
                      setSelectedPropertyTypesBuySold([...buyPropertyTypes]);
                      setPropertyTypePillTouchedBuySold(false);
                    }
                    setPropertyTypeWarning('');
                  } else {
                    setPropertyTypeWarning('');
                  }
                }}
              >
                <ArrowLeft className="h-6 w-6 text-blue-700" />
              </button>
            </DialogClose>
            <DialogTitle className="text-2xl font-bold text-center mb-6 pt-8">Property Class</DialogTitle>
            <button
              className={`absolute top-4 right-4 text-primary font-bold text-lg focus:outline-none transition-transform ${resetBouncing ? 'animate-bounce' : ''}`}
              onClick={() => {
                if ((activeFilterTab as string) === 'rent') {
                  setSelectedPropertyTypesRent([...rentPropertyTypes]);
                  setPropertyTypePillTouchedRent(false);
                } else if (activeFilterTab === 'buy' || activeFilterTab === 'sold') {
                  setSelectedPropertyTypesBuySold([...buyPropertyTypes]);
                  setPropertyTypePillTouchedBuySold(false);
                }
                setMinSquareFeet('');
                setMaxSquareFeet('');
                setResetBouncing(true);
                setTimeout(() => setResetBouncing(false), 400);
              }}
              type="button"
            >
              Reset
            </button>
            {/* Show warning message below the divider if present */}
            {propertyTypeWarning && (
              <div className="flex items-center justify-center gap-2 bg-red-100 border border-red-300 text-red-700 rounded-lg px-4 py-2 my-2 font-semibold text-sm">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9z" /></svg>
                {propertyTypeWarning}
              </div>
            )}
            <div className="border-b border-gray-200 mb-4"></div>
            <div className="h-full px-6 pb-32 flex-1 flex flex-col justify-between overflow-y-auto">
              <div className="flex flex-col gap-4">
                {propertyTypes.map((type) => (
                  <button
                    key={type}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      togglePropertyType(type);
                    }}
                    className={`flex items-center justify-between w-full px-4 py-3 mb-2 rounded-xl text-left border transition-colors
                      ${getSelectedPropertyTypes().includes(type)
                        ? 'border-primary bg-blue-50'
                        : 'border-border bg-background'}
                    `}
                    tabIndex={0}
                    aria-pressed={getSelectedPropertyTypes().includes(type)}
                  >
                    <span className="text-base">{type}</span>
                    <span
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors duration-150 ${getSelectedPropertyTypes().includes(type) ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}
                      tabIndex={0}
                      role="checkbox"
                      aria-checked={getSelectedPropertyTypes().includes(type)}
                    >
                      {getSelectedPropertyTypes().includes(type) && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                      )}
                    </span>
                    <input
                      type="checkbox"
                      checked={getSelectedPropertyTypes().includes(type)}
                      onChange={() => {}}
                      className="sr-only"
                      tabIndex={-1}
                    />
                  </button>
                ))}
              </div>
            </div>
            {/* Floating Save Button */}
            <div className="sticky bottom-0 left-0 right-0 z-10 bg-background shadow-lg px-6 py-4 flex justify-center">
              <button
                className="bg-primary text-white rounded-full px-8 py-3 font-bold text-lg shadow hover:bg-blue-700 transition-colors w-full max-w-md"
                onClick={() => setShowPropertyTypeModal(false)}
              >
                Save
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Amenities Modal removed */}

      {/* Square Feet Modal */}
      {showSquareFeetModal && (
        <Dialog open={showSquareFeetModal} onOpenChange={(open) => {
          if (!open && !isSavingSquareFeet) {
            // Only reset if we're not explicitly saving
            // This handles escape key and outside clicks
            setPendingMinSquareFeet(minSquareFeet);
            setPendingMaxSquareFeet(maxSquareFeet);
          }
          setShowSquareFeetModal(open);
        }}>
          <DialogContent
            className="relative w-full max-w-md bg-background rounded-3xl shadow-2xl p-6 flex flex-col max-h-[60vh] overflow-y-auto"
            hideDefaultClose={true}
          >
            <button 
              className="flex items-center justify-center bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors absolute top-4 left-4"
              aria-label="Close square feet modal"
              style={{ height: '40px', width: '40px' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // Reset pending values to current values when closing without saving
                setPendingMinSquareFeet(minSquareFeet);
                setPendingMaxSquareFeet(maxSquareFeet);
                setShowSquareFeetModal(false);
              }}
            >
              <ArrowLeft className="h-6 w-6 text-blue-700" />
            </button>
            <DialogTitle className="text-2xl font-bold text-center mb-6">Square Feet</DialogTitle>
            {/* Add min/max square feet input fields with sliders inside */}
            <div className="flex flex-col gap-4 mt-4">
              <div>
                <label className="block text-base font-medium mb-1">Minimum square feet</label>
                <div className="relative w-full">
                  <input
                    type="number"
                    min={0}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 text-base bg-transparent relative z-10"
                    placeholder="Enter minimum square feet"
                    value={pendingMinSquareFeet}
                    onChange={e => setPendingMinSquareFeet(e.target.value.replace(/[^0-9]/g, ''))}
                    style={{ position: 'relative', zIndex: 2 }}
                  />
                  <input
                    type="range"
                    min={0}
                    max={7500}
                    step={100}
                    value={pendingMinSquareFeet || 0}
                    onChange={e => setPendingMinSquareFeet(e.target.value)}
                    className="absolute left-0 top-0 w-full h-full opacity-40 cursor-pointer"
                    style={{ zIndex: 3 }}
                  />
                </div>
              </div>
              <div>
                <label className="block text-base font-medium mb-1">Maximum square feet</label>
                <div className="relative w-full">
                  <input
                    type="number"
                    min={0}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2 text-base bg-transparent relative z-10"
                    placeholder="Enter maximum square feet"
                    value={pendingMaxSquareFeet}
                    onChange={e => setPendingMaxSquareFeet(e.target.value.replace(/[^0-9]/g, ''))}
                    style={{ position: 'relative', zIndex: 2 }}
                  />
                  <input
                    type="range"
                    min={0}
                    max={10000}
                    step={100}
                    value={pendingMaxSquareFeet || 0}
                    onChange={e => setPendingMaxSquareFeet(e.target.value)}
                    className="absolute left-0 top-0 w-full h-full opacity-40 cursor-pointer"
                    style={{ zIndex: 3 }}
                  />
                </div>
              </div>
              <button
                className="bg-primary text-white rounded-full px-5 py-2 font-bold text-base shadow hover:bg-blue-700 transition-colors mt-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Set flag to indicate we're saving
                  setIsSavingSquareFeet(true);
                  // Save the pending values to the actual state
                  setMinSquareFeet(pendingMinSquareFeet);
                  setMaxSquareFeet(pendingMaxSquareFeet);
                  setShowSquareFeetModal(false);
                  // Reset flag after a short delay
                  setTimeout(() => setIsSavingSquareFeet(false), 100);
                }}
              >
                Save
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Days on Market Modal */}
      {showDaysOnMarketModal && (
        <Dialog
          open={showDaysOnMarketModal}
          onOpenChange={open => {
            setShowDaysOnMarketModal(open);
          }}
        >
          <DialogContent
            className={`fixed left-1/2 top-[60%] -translate-x-1/2 max-w-md w-full min-h-[80vh] flex flex-col bg-background rounded-3xl shadow-2xl p-0 overflow-hidden text-foreground border border-border max-h-[80vh] overflow-y-auto`}
            hideDefaultClose={true}
          >
            <DialogClose asChild>
              <button 
                className="flex items-center justify-center bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors absolute top-4 left-4"
                aria-label="Close days on market modal"
                style={{ height: '40px', width: '40px' }}
              >
                <ArrowLeft className="h-6 w-6 text-blue-700" />
              </button>
            </DialogClose>
            <DialogTitle className="text-2xl font-bold text-center mb-6 pt-8">Days on Market</DialogTitle>
            <button
              className={`absolute top-4 right-4 text-primary font-bold text-lg focus:outline-none transition-transform ${daysOnMarketResetBouncing ? 'animate-bounce' : ''}`}
              onClick={() => {
                setPendingDaysOnMarket(null);
                setDaysOnMarketResetBouncing(true);
                setTimeout(() => setDaysOnMarketResetBouncing(false), 400);
              }}
              type="button"
            >
              Reset
            </button>
            <div className="border-b border-gray-200 mb-4"></div>
            <div className="h-full px-6 pb-32 flex-1 flex flex-col justify-between overflow-y-auto">
              <div className="flex flex-col gap-4">
                <label className="block text-base font-medium mb-1">Show listings on market up to:</label>
                <div className="flex flex-col gap-2 mt-2">
                  {[
                    { label: 'Any', value: null },
                    { label: '7 Days', value: 7 },
                    { label: '14 Days', value: 14 },
                    { label: '21 Days', value: 21 },
                    { label: '30 Days', value: 30 },
                    { label: '90 Days', value: 90 },
                    { label: '6 Months', value: 180 },
                    { label: '12 Months+', value: 365 },
                  ].map(option => (
                    <button
                      key={option.label}
                      type="button"
                      className={`flex items-center justify-between w-full p-0 rounded-xl text-left border mb-2 px-4 py-3 transition-colors
                        ${pendingDaysOnMarket === option.value
                          ? 'border-primary bg-blue-50'
                          : 'border-border bg-background'}
                      `}
                      onClick={() => setPendingDaysOnMarket(option.value)}
                      tabIndex={0}
                      aria-pressed={pendingDaysOnMarket === option.value}
                    >
                      <span className="text-base">{option.label}</span>
                      <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${pendingDaysOnMarket === option.value ? 'border-primary bg-primary' : 'border-gray-300 bg-white'}`}>
                        {pendingDaysOnMarket === option.value && (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" /></svg>
                        )}
                      </span>
                      {/* Visually hidden radio for accessibility */}
                      <input
                        type="radio"
                        name="daysOnMarket"
                        checked={pendingDaysOnMarket === option.value}
                        onChange={() => setPendingDaysOnMarket(option.value)}
                        className="sr-only"
                        tabIndex={-1}
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {/* Floating Save Button */}
            <div className="sticky bottom-0 left-0 right-0 z-10 bg-background shadow-lg px-6 py-4 flex justify-center">
              <button
                className="bg-primary text-white rounded-full px-8 py-3 font-bold text-lg shadow hover:bg-blue-700 transition-colors w-full max-w-md"
                onClick={() => {
                  setFilters((prev: any) => ({ ...prev, daysOnMarket: pendingDaysOnMarket }));
                  setShowDaysOnMarketModal(false);
                }}
              >
                Save
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
} 